import {FC, useCallback} from "react";

import {DEFAULT_MAP_CONFIG} from "common/geo/components/google-maps/constants";
import {GoogleMaps} from "common/geo/components/google-maps/GoogleMaps";
import config from "config";
import {ClickEventValue} from "google-map-react";

import {FleetOwnerPortalService, OrderFleetService} from "@bolteu/bolt-server-api-fleet-owner-portal";

import styles from "../style.module.css";
import {DriverInfo} from "../types";
import {useDriverMapPolylines} from "./hooks/useDriverMapPolylines";
import {useMapView} from "./hooks/useMapView";

interface ResponsiveLiveMapProps {
    drivers: FleetOwnerPortalService.DriverForFleetOwner[];
    selectedDriver: DriverInfo | null;
    previewDriverId: number | null;
    isMobileView: boolean;
    isMapView: boolean;
    onDriverSelected: (driver: FleetOwnerPortalService.DriverForFleetOwner) => void;
    onDriverPreviewSelected?: (driverId: number | null) => void;
    lastDropoffAndWaitingTime: OrderFleetService.GetLastDropoffAndWaitingTimeResponse | null;
    isDriverRoutesShown: boolean;
    driverTrailPeriodMs: number;
}

export const ResponsiveMapView: FC<ResponsiveLiveMapProps> = ({
    drivers,
    selectedDriver,
    previewDriverId,
    isMobileView,
    isMapView,
    onDriverSelected,
    onDriverPreviewSelected,
    lastDropoffAndWaitingTime,
    isDriverRoutesShown,
    driverTrailPeriodMs,
}) => {
    const {markers, onGoogleApiLoaded, handleZoomEnd, handleOnDrag, driverLocationHistory, googleMapsApi} = useMapView(
        drivers,
        selectedDriver,
        previewDriverId,
        isMobileView,
        isMapView,
        lastDropoffAndWaitingTime,
        isDriverRoutesShown,
        driverTrailPeriodMs,
        onDriverSelected,
        onDriverPreviewSelected,
    );

    useDriverMapPolylines(googleMapsApi, driverLocationHistory, isDriverRoutesShown);

    const googleMapsOnClick = useCallback(
        (value: ClickEventValue) => {
            // google-maps library typed value.event as any
            const clickEvent = value.event as {target: {id: string}};
            const elementId = clickEvent.target.id;
            if (
                previewDriverId &&
                typeof elementId === "string" &&
                !elementId.includes("driver-marker-") &&
                onDriverPreviewSelected
            ) {
                onDriverPreviewSelected(null);
            }
        },
        [onDriverPreviewSelected, previewDriverId],
    );

    const shouldMockGoogleMaps = config.isStageMockoon;
    if (shouldMockGoogleMaps) {
        return <iframe title="blank page" className="h-[calc(100dvh-56px)] w-screen" />;
    }

    return (
        <div className={styles["live-map"]}>
            <GoogleMaps
                options={{
                    mapTypeId: "roadmap",
                    clickableIcons: false,
                    streetViewControl: false,
                    fullscreenControl: false,
                    maxZoom: 14,
                    disableDefaultUI: isMobileView,
                    zoomControlOptions: {position: 3},
                }}
                onClick={googleMapsOnClick}
                defaultCenter={DEFAULT_MAP_CONFIG.center}
                defaultZoom={DEFAULT_MAP_CONFIG.zoom}
                yesIWantToUseGoogleMapApiInternals
                onGoogleApiLoaded={onGoogleApiLoaded}
                onZoomAnimationEnd={handleZoomEnd}
                onDragEnd={handleOnDrag}
            >
                {markers}
            </GoogleMaps>
        </div>
    );
};
